import ImpactPill from "@/components/Impact/ImpactPill";
import RootModal from "@/components/RootModal/RootModal";
import useAnalytics from "@/hooks/useAnalytics";
import useTranslation from "@/hooks/useTranslation";
import styles from "@/pages/materials/MaterialImpactModal/MaterialImpactReferenceModal.module.scss";
import MaterialMatchCascader from "@/pages/packaging_materials/PackagingMaterialDetail/MaterialMatchCascader";
import { getOption } from "@/pages/packaging_materials/PackagingMaterialDetail/PackagingMaterialImpact";
import { PackagingMaterial } from "@/pages/packaging_materials/PackagingMaterialOverview/PackagingMaterialsOverview";
import { EditOutlined } from "@ant-design/icons";
import { Button, Col, message, Row } from "antd";
import React, { useEffect, useState } from "react";
import {
  PackagingMaterialMatchOption,
  useBulkPatchPackagingMaterial,
  useGetPackagingMaterialOptions,
} from "../usePackagingMaterials";

export interface MaterialImpactReferenceModalProps {
  materials: PackagingMaterial[];
  children?: React.ReactElement;
}

export default function MaterialImpactModal({
  materials,
  children,
}: MaterialImpactReferenceModalProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const selectedKeys = materials?.map((material) => material.id);
  const analytics = useAnalytics();
  const translation = useTranslation();
  const bulkPatch = useBulkPatchPackagingMaterial();
  const [selectedOption, setSelectedOption] = useState<PackagingMaterialMatchOption | undefined>(
    undefined
  );
  const { data: optionsData, isLoading: optionsIsLoading } = useGetPackagingMaterialOptions();
  const [selectedDataset, setSelectedDataset] = useState<number | undefined>();

  useEffect(() => {
    if (materials?.length === 1 && materials[0].match?.id) {
      setSelectedDataset(materials[0].match.id);
      setSelectedOption(getOption(materials[0].match.id, optionsData));
    } else {
      setSelectedDataset(undefined);
      setSelectedOption(undefined);
    }
  }, [modalOpen]);

  const onClose = () => {
    setModalOpen(false);
  };

  const onSubmit = () => {
    if (selectedOption) {
      return bulkPatch
        .mutateAsync({
          materialIds: selectedKeys,
          patch: {
            impact_dataset_id: selectedOption.dataset_id,
          },
        })
        .then(() => {
          analytics.track(`Packaging Material Reference Updated`);
          message.success(`Impact reference${materials.length > 1 ? "s" : ""} Updated.`);
          onClose?.();
        })
        .catch(() => {
          message.error(translation.errors.general);
        });
    }
  };

  const onDatasetChange = (_: any, selectedOptions: PackagingMaterialMatchOption[] | undefined) => {
    if (selectedOptions) {
      setSelectedOption(selectedOptions[selectedOptions.length - 1]);
    }
  };

  return (
    <>
      <span onClick={() => setModalOpen(true)}>
        {children || (
          <Button
            onClick={() => setModalOpen(true)}
            type={"primary"}
            icon={<EditOutlined />}
            disabled={materials?.length === 0}
          >
            Edit impact reference
          </Button>
        )}
      </span>

      <RootModal
        open={modalOpen}
        width={750}
        onCancel={() => onClose()}
        okText={"Save"}
        keyboard={false}
        zIndex={998}
        destroyOnHidden={true}
        title={
          materials?.length === 0 || materials?.length === 1
            ? "Edit impact reference"
            : `Edit impact references for ${materials?.length} materials`
        }
        onOk={onSubmit}
        okButtonProps={{ loading: optionsIsLoading || bulkPatch.isLoading }}
      >
        <Row>
          <Col span={24}>
            <p>
              We need to connect your packaging materials to our impact reference database in order
              to calculate the packaging impact.
            </p>
          </Col>
          <Col span={24}>
            <Row
              className={styles["impactContainer"]}
              align={"middle"}
            >
              <Col span={24}>
                <MaterialMatchCascader
                  dataset_id={selectedDataset}
                  onChange={onDatasetChange}
                />
              </Col>
              <Col className={styles["impactRow"]}>
                <ImpactPill
                  impact={selectedOption?.impact}
                  type="borderless"
                  loading={optionsIsLoading}
                  before={
                    selectedOption && (
                      <span className={styles.impactUnit}>1 {selectedOption.unit}</span>
                    )
                  }
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </RootModal>
    </>
  );
}
